import { useEffect, useRef, useState } from "react";
import { cn } from "../cn";
import { useSession } from "./useSession";
import { useT } from "../../i18n";
import { activityApi, type ActivityEvent } from "../../lib/activity-api";
import {
  notifyActivityRead,
  useActivityNotifications,
} from "../useActivityNotifications";
import { gotoDeepLink, timeAgo } from "../activity-format";

/**
 * Marketplace Notification Center (BUG4/6).
 *
 * SINGLE canonical source of truth: it consumes the SAME `activity_events`
 * identity + read/unread state as the global headbar bell (via
 * `useActivityNotifications`). There is NO independent counter and NO duplicate
 * record — marking an item read here decrements the headbar badge instantly
 * (and vice-versa), and unread=0 clears the red dot everywhere. Read state is
 * server-authoritative, so it persists across refresh/restart.
 */
export function MarketplaceNotifications() {
  const t = useT();
  const session = useSession();
  const { unreadCount, recent, refresh } = useActivityNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!session.isAuthenticated) return null;

  const onItem = async (ev: ActivityEvent) => {
    setOpen(false);
    if (!ev.read) {
      try {
        await activityApi.markRead(ev.id);
        notifyActivityRead(); // sync the headbar bell + this center at once
      } catch {
        /* fail-closed; badge refreshes on next poll */
      }
    }
    gotoDeepLink(ev);
  };

  const onMarkAll = async () => {
    try {
      await activityApi.markAllRead();
      notifyActivityRead();
      refresh();
    } catch {
      /* ignore */
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="marketplace-notif-btn"
        aria-label={unreadCount ? t("marketplace.nav.notificationsCountAria", { count: unreadCount }) : t("marketplace.nav.notifications")}
        onClick={() => setOpen((v) => !v)}
        className="relative h-9 w-9 inline-flex items-center justify-center rounded-md text-text-secondary hover:text-neon-teal hover:bg-elevated/60 transition-colors"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
          <path d="M8 2a4 4 0 00-4 4v2.5L3 11h10l-1-2.5V6a4 4 0 00-4-4zM6.5 13a1.5 1.5 0 003 0" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
        </svg>
        {unreadCount > 0 && (
          <span data-testid="marketplace-notif-dot" className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-neon-coral text-[9px] font-bold text-black inline-flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="marketplace-notif-panel" className="absolute right-0 mt-2 w-80 z-modal neon-glass-strong rounded-xl border border-border-strong shadow-elevated overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-divider">
            <span className="font-mono text-[9.5px] tracking-[0.24em] uppercase text-neon-teal">{t("marketplace.nav.notifications")}</span>
            {unreadCount > 0 && (
              <button
                type="button"
                data-testid="marketplace-notif-mark-all"
                onClick={onMarkAll}
                className="font-mono text-[9px] uppercase tracking-[0.14em] text-text-muted hover:text-neon-teal"
              >
                {t("marketplace.notifications.markAll")}
              </button>
            )}
          </div>
          {recent.length === 0 ? (
            <p className="px-4 py-6 text-center font-mono text-[11px] text-text-muted">{t("marketplace.notifications.allCaughtUp")}</p>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-border-divider">
              {recent.map((ev) => (
                <button
                  key={ev.id}
                  type="button"
                  data-testid="marketplace-notif-item"
                  data-read={ev.read}
                  onClick={() => onItem(ev)}
                  className="w-full text-left px-4 py-3 hover:bg-elevated/60 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {!ev.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-neon-coral" aria-hidden />}
                    <span className={cn("font-display text-[13px]", ev.read ? "text-text-secondary" : "text-text-primary")}>{ev.title}</span>
                    <span className="ml-auto shrink-0 font-mono text-[9px] text-text-muted">{timeAgo(ev.event_time)}</span>
                  </div>
                  <p className="mt-0.5 font-mono text-[10px] text-text-muted">{ev.body}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
